/** Editor для Реалізації послуг з переробки (Порція 4b). */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs as refsApi } from '@/api/refs';
import { accounts as accountsApi } from '@/api/documents';
import {
  processingServicesSales,
  type ProcessingServicesSalesMaterialIn,
  type ProcessingServicesSalesProductIn,
} from '@/api/processingServicesSales';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DatePopover from '@/components/DatePopover';
import DocumentToolbar from '@/shared/components/DocumentToolbar';

const PERMISSION = 'production.processing.service.manage';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  customer_contractor_id: string;
  contract_id: string;
  warehouse_id: string;
  service_amount: string;
  vat_amount: string;
  revenue_account_code: string | null;
  receivable_account_code: string | null;
  comment: string;
  materials: ProcessingServicesSalesMaterialIn[];
  products: ProcessingServicesSalesProductIn[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  customer_contractor_id: '',
  contract_id: '',
  warehouse_id: '',
  service_amount: '0',
  vat_amount: '0',
  revenue_account_code: null,
  receivable_account_code: null,
  comment: '',
  materials: [],
  products: [],
};

const newM = (n: number): ProcessingServicesSalesMaterialIn => ({
  line_number: n, nomenclature_id: '', quantity: '0', declared_value: '0',
});
const newP = (n: number): ProcessingServicesSalesProductIn => ({
  line_number: n, nomenclature_id: '', quantity: '0',
});

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}

export default function ProcessingServicesSalesEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs, totalAccessibleOrgs } = usePermissionsByOrg(PERMISSION);
  const cQ = useQuery({ queryKey: ['contractors-all'], queryFn: () => contractorsApi.list({ limit: 1000 }) });
  const nomQ = useQuery({ queryKey: ['nomenclature-full'], queryFn: () => nomApi.list({ limit: 500 }) });
  const accountsQ = useQuery({
    queryKey: ['accounts-postable'],
    queryFn: () => accountsApi.list({ only_postable: true, limit: 1000 }),
  });
  const acctList = accountsQ.data ?? [];
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const warehousesQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refsApi.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });
  const docQuery = useQuery({
    queryKey: ['processing-services-sales', id],
    queryFn: () => processingServicesSales.get(id!),
    enabled: !isNew,
  });
  const numberPreview = useNumberPreview('doc.processing_services_sales', form.organization_id, isNew);

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);
  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        customer_contractor_id: d.customer_contractor_id,
        contract_id: d.contract_id ?? '',
        warehouse_id: d.warehouse_id,
        service_amount: d.service_amount,
        vat_amount: d.vat_amount,
        revenue_account_code: d.revenue_account_code ?? null,
        receivable_account_code: d.receivable_account_code ?? null,
        comment: d.comment ?? '',
        materials: d.materials.map((m) => ({
          line_number: m.line_number, nomenclature_id: m.nomenclature_id, quantity: m.quantity,
          declared_value: m.declared_value ?? '0',
        })),
        products: d.products.map((p) => ({
          line_number: p.line_number, nomenclature_id: p.nomenclature_id, quantity: p.quantity,
        })),
      });
    }
  }, [isNew, docQuery.data]);

  const isPosted = docQuery.data?.posted ?? false;

  const acctSelect = (
    value: string | null | undefined,
    onChange: (v: string | null) => void,
    def: string,
  ) => (
    <select value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} disabled={isPosted}>
      <option value="">— (деф. {def})</option>
      {acctList.map((a) => (<option key={a.id} value={a.code}>{a.code} — {a.name}</option>))}
    </select>
  );

  const buildPayload = () => ({
    document_number: form.document_number || null,
    document_date: new Date(form.document_date).toISOString(),
    customer_contractor_id: form.customer_contractor_id,
    contract_id: form.contract_id || null,
    warehouse_id: form.warehouse_id,
    service_amount: form.service_amount,
    vat_amount: form.vat_amount,
    revenue_account_code: form.revenue_account_code,
    receivable_account_code: form.receivable_account_code,
    comment: form.comment || null,
    materials: form.materials,
    products: form.products,
  });

  const create = useMutation({
    mutationFn: () => processingServicesSales.create({ organization_id: form.organization_id, ...buildPayload() }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['processing-services-sales'] });
      navigate(`/processing-services-sales/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => processingServicesSales.update(id!, buildPayload()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['processing-services-sales', id] });
    },
    onError: (e) => setError(extractError(e)),
  });
  // Новий: створити і одразу провести (create → post → картка).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await processingServicesSales.create({ organization_id: form.organization_id, ...buildPayload() });
      await processingServicesSales.post(doc.id);
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['processing-services-sales'] });
      navigate(`/processing-services-sales/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений непроведений: зберегти правки і одразу провести (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await processingServicesSales.update(id!, buildPayload());
      await processingServicesSales.post(id!);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['processing-services-sales'] }),
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => processingServicesSales.unpost(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['processing-services-sales'] }),
    onError: (e) => setError(extractError(e)),
  });

  function addM() { setForm((f) => ({ ...f, materials: [...f.materials, newM(f.materials.length + 1)] })); }
  function rmM(i: number) {
    setForm((f) => ({ ...f, materials: f.materials.filter((_, idx) => idx !== i).map((it, idx) => ({ ...it, line_number: idx + 1 })) }));
  }
  function setM(i: number, patch: Partial<ProcessingServicesSalesMaterialIn>) {
    setForm((f) => ({ ...f, materials: f.materials.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }));
  }
  function addP() { setForm((f) => ({ ...f, products: [...f.products, newP(f.products.length + 1)] })); }
  function rmP(i: number) {
    setForm((f) => ({ ...f, products: f.products.filter((_, idx) => idx !== i).map((it, idx) => ({ ...it, line_number: idx + 1 })) }));
  }
  function setP(i: number, patch: Partial<ProcessingServicesSalesProductIn>) {
    setForm((f) => ({ ...f, products: f.products.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }));
  }

  if (!isNew && docQuery.isLoading) return <div>Завантаження…</div>;

  return (
    <div>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова реалізація послуги переробки' : `Реалізація: ${form.document_number}`}
          {isPosted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>✓ Проведено</span>
          )}
        </h2>
        <DocumentToolbar
          onBackToList={() => navigate('/processing-services-sales')}
          onSave={() => (isNew ? create.mutate() : update.mutate())}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => (isNew ? createAndPost.mutate() : updateAndPost.mutate())}
          postPending={create.isPending || createAndPost.isPending || updateAndPost.isPending || update.isPending}
          postTitle="Зберігає та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          deletable={false}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
        <div className="field">
          <label>Юрособа</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })} disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
            </small>
          )}
        </div>
        <div className="field">
          <label>Номер</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''} disabled={isPosted} />
        </div>
        <div className="field">
          <label>Дата</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })} disabled={isPosted} />
        </div>
        <div className="field">
          <label>Замовник</label>
          <select value={form.customer_contractor_id}
                  onChange={(e) => setForm({ ...form, customer_contractor_id: e.target.value })} disabled={isPosted}>
            <option value="">—</option>
            {cQ.data?.items.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
          </select>
        </div>
        <div className="field">
          <label>Склад</label>
          <select value={form.warehouse_id}
                  onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })} disabled={isPosted}>
            <option value="">—</option>
            {warehousesQ.data?.map((w) => (<option key={w.id} value={w.id}>{w.code} — {w.name}</option>))}
          </select>
        </div>
        <div className="field">
          <label>Сума послуги</label>
          <input type="number" step="0.01" value={form.service_amount}
                 onChange={(e) => setForm({ ...form, service_amount: e.target.value })} disabled={isPosted} />
        </div>
        <div className="field">
          <label>ПДВ</label>
          <input type="number" step="0.01" value={form.vat_amount}
                 onChange={(e) => setForm({ ...form, vat_amount: e.target.value })} disabled={isPosted} />
        </div>
        <div className="field">
          <label>Рахунок доходів</label>
          {acctSelect(form.revenue_account_code, (v) => setForm({ ...form, revenue_account_code: v }), '703')}
        </div>
        <div className="field">
          <label>Рахунок розрахунків</label>
          {acctSelect(form.receivable_account_code, (v) => setForm({ ...form, receivable_account_code: v }), '361')}
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })} disabled={isPosted} />
        </div>
      </div>

      <div style={{ marginTop: '1rem', fontWeight: 600 }}>Чужа сировина (з 022)</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem', width: 40 }}>#</th>
            <th style={{ padding: '0.5rem' }}>Сировина</th>
            <th style={{ padding: '0.5rem', width: 100 }}>К-сть</th>
            <th style={{ padding: '0.5rem', width: 130 }}>Декл. вартість</th>
            <th style={{ padding: '0.5rem', width: 40 }} />
          </tr>
        </thead>
        <tbody>
          {form.materials.map((m, i) => (
            <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ padding: '0.5rem' }}>{m.line_number}</td>
              <td style={{ padding: '0.5rem' }}>
                <select value={m.nomenclature_id}
                        onChange={(e) => setM(i, { nomenclature_id: e.target.value })} disabled={isPosted}>
                  <option value="">—</option>
                  {nomQ.data?.items.map((n) => (<option key={n.id} value={n.id}>{n.code} — {n.name}</option>))}
                </select>
              </td>
              <td style={{ padding: '0.5rem' }}>
                <input type="number" step="0.001" value={m.quantity}
                       onChange={(e) => setM(i, { quantity: e.target.value })} disabled={isPosted} />
              </td>
              <td style={{ padding: '0.5rem' }}>
                <input type="number" step="0.01" value={m.declared_value ?? '0'}
                       onChange={(e) => setM(i, { declared_value: e.target.value })} disabled={isPosted} />
              </td>
              <td style={{ padding: '0.5rem' }}>{!isPosted && <button onClick={() => rmM(i)}>×</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!isPosted && <button onClick={addM} style={{ marginTop: '0.5rem' }}>+ Сировина</button>}

      <div style={{ marginTop: '1rem', fontWeight: 600 }}>Чужа продукція (повертаємо клієнту)</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem', width: 40 }}>#</th>
            <th style={{ padding: '0.5rem' }}>Продукція</th>
            <th style={{ padding: '0.5rem', width: 100 }}>К-сть</th>
            <th style={{ padding: '0.5rem', width: 40 }} />
          </tr>
        </thead>
        <tbody>
          {form.products.map((p, i) => (
            <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ padding: '0.5rem' }}>{p.line_number}</td>
              <td style={{ padding: '0.5rem' }}>
                <select value={p.nomenclature_id}
                        onChange={(e) => setP(i, { nomenclature_id: e.target.value })} disabled={isPosted}>
                  <option value="">—</option>
                  {nomQ.data?.items.map((n) => (<option key={n.id} value={n.id}>{n.code} — {n.name}</option>))}
                </select>
              </td>
              <td style={{ padding: '0.5rem' }}>
                <input type="number" step="0.001" value={p.quantity}
                       onChange={(e) => setP(i, { quantity: e.target.value })} disabled={isPosted} />
              </td>
              <td style={{ padding: '0.5rem' }}>{!isPosted && <button onClick={() => rmP(i)}>×</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!isPosted && <button onClick={addP} style={{ marginTop: '0.5rem' }}>+ Продукція</button>}

      {error && <div className="error" style={{ marginTop: '1rem' }}>{error}</div>}
    </div>
  );
}
